/**
 * Claim Right -- pre-submission 837 scrubber inside the workflow shell.
 *
 * Route: /ClaimRight
 *
 * 4-step shell:
 *   1. Pick a sample, OR
 *   2. Upload a draft 837 (.json / .x12 / .csv) which we read into the
 *      payload textarea, OR paste the JSON directly.
 *   3. Run the scrub against NCCI / MUE / modifier rules (scrubClaim).
 *   4. Issue list with severity + fix hints; download JSON / report; chat
 *      with the issue summary as case context.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WorkflowLayout,
  FileDropZone,
  ResultsPanel,
  Icon,
  ExampleModal,
  getWorkflowModule,
} from '../../components/workflow';
import { scrubClaim } from '../../services/suiteApi';
import { getSampleById } from '../../fixtures/testCases';
import { CLAIMRIGHT_EXAMPLE } from '../../fixtures/exampleInputs';
import './suite.css';

const SAMPLE_PAYLOAD = JSON.stringify({
  claimInformation: {
    serviceLines: [
      { professionalService: { procedureCode: '99214', serviceUnitCount: 1, procedureModifiers: ['25'] } },
      { professionalService: { procedureCode: '11042', serviceUnitCount: 2 } },
    ],
    healthCareCodeInformation: [{ diagnosisCode: 'M54.5' }, { diagnosisCode: 'E11.9' }],
  },
}, null, 2);

const KIND_META = {
  ncci: { label: 'NCCI / CCI' },
  mue:  { label: 'MUE' },
  global_period: { label: 'Global period' },
  medical_necessity: { label: 'Medical necessity' },
  pos: { label: 'Place of service' },
  modifier: { label: 'Modifier' },
};

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

function tryParseToPayload(text) {
  const t = (text || '').trim();
  if (!t) return null;
  try {
    return JSON.parse(t);
  } catch { /* fall through to CSV/X12 heuristic */ }
  const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const serviceLines = lines
    .filter((l) => /^\d{5}|^[A-Z]\d{4}/.test(l))
    .map((l) => {
      const cols = l.split(/[,;\t]/).map((c) => c.trim());
      const code = cols[0];
      const units = Number(cols[1] || 1);
      const modifiers = (cols[2] || '').split(/\s|\|/).map((s) => s.trim()).filter(Boolean);
      return { professionalService: { procedureCode: code, serviceUnitCount: units, procedureModifiers: modifiers } };
    });
  if (!serviceLines.length) return null;
  return { claimInformation: { serviceLines, healthCareCodeInformation: [] } };
}

function summariseResult(payloadText, result, sample) {
  if (!result) return '';
  const issues = result.issues || [];
  const header = sample
    ? `CLAIM RIGHT — sample: ${sample.title}`
    : `CLAIM RIGHT — scrubbed claim`;
  const counts = issues.reduce((acc, i) => { acc[i.severity || 'review'] = (acc[i.severity || 'review'] || 0) + 1; return acc; }, {});
  const top = issues.slice(0, 6).map((i) => `${(i.severity || 'review').toUpperCase()} · ${KIND_META[i.kind]?.label || i.kind} — ${i.message}${i.fix ? ` (fix: ${i.fix})` : ''}`).join('\n');
  return [
    header,
    `Issues: ${issues.length} total (${counts.block || 0} blocking, ${counts.review || 0} to review)`,
    `NCCI pairs checked: ${(result.ncci_pairs || []).length}`,
    `MUE caps applied: ${Object.keys(result.mue_caps || {}).length}`,
    '',
    top || 'No issues flagged.',
  ].join('\n');
}

export default function ClaimRightPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleDef = getWorkflowModule('claim-right');

  const [files, setFiles] = useState([]);
  const [payloadText, setPayloadText] = useState(SAMPLE_PAYLOAD);
  const [showExample, setShowExample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busySampleId, setBusySampleId] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [sample, setSample] = useState(null);
  const printRef = useRef(null);

  const sampleId = searchParams.get('sample');

  const runScrub = async (payload, opts = {}) => {
    setBusy(true); setError(''); setResult(null);
    try {
      const data = await scrubClaim({ payload });
      if (!data?.success) throw new Error(data?.message || 'Scrub failed');
      setResult(data.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(false);
      if (opts.sampleId) setBusySampleId(null);
    }
  };

  const onPickSample = async (s) => {
    setBusySampleId(s.id);
    setSample(s);
    const txt = JSON.stringify(s.payload.payload, null, 2);
    setPayloadText(txt);
    await runScrub(s.payload.payload, { sampleId: s.id });
  };

  useEffect(() => {
    if (!sampleId) return;
    const s = getSampleById(sampleId);
    if (s && s.moduleId === 'claim-right') onPickSample(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleId]);

  // When a file is added, attempt to parse it into the payload textarea.
  useEffect(() => {
    if (!files.length) return;
    (async () => {
      try {
        const txt = await readFileAsText(files[0]);
        const parsed = tryParseToPayload(txt);
        if (parsed) {
          setPayloadText(JSON.stringify(parsed, null, 2));
          setError('');
        } else {
          setError('Could not auto-parse the file. Paste JSON or CSV (code,units,modifiers) directly.');
        }
      } catch (err) {
        setError(err?.message || 'Could not read file');
      }
    })();
  }, [files]);

  const onRun = async () => {
    setSample(null);
    if (sampleId) setSearchParams({});
    let payload;
    try { payload = JSON.parse(payloadText); }
    catch (e) { setError('Payload is not valid JSON: ' + e.message); return; }
    runScrub(payload);
  };

  const currentStep = result ? 'results' : (payloadText.trim() ? 'run' : 'upload');
  const chatContext = useMemo(() => summariseResult(payloadText, result, sample), [payloadText, result, sample]);

  return (
    <WorkflowLayout
      module={moduleDef}
      currentStep={currentStep}
      caseContext={chatContext}
    >
      <section className="wf-step-card">
        <header className="wf-step-card__head">
          <h2 className="wf-step-card__title">
            <span className="wf-step-card__num">2</span>
            Bring in the draft 837
          </h2>
        </header>
        <p className="wf-step-card__hint">{moduleDef.uploadHint}</p>
        <FileDropZone
          files={files}
          onFiles={setFiles}
          accept={moduleDef.accepts}
          multiple={false}
          hint="JSON 837 payload, raw X12, or a CSV of service lines (code, units, modifiers)."
        />
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dm-muted)' }}>
              Claim payload (JSON 837)
            </label>
            <button type="button" className="example-link" onClick={() => setShowExample(true)}>View Example</button>
          </div>
          <textarea
            value={payloadText}
            onChange={(e) => setPayloadText(e.target.value)}
            style={{
              width: '100%', minHeight: 220, padding: 10, border: '1px solid var(--dm-border)',
              borderRadius: 8, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12, lineHeight: 1.5, color: 'var(--dm-ink)',
            }}
          />
        </div>
        <div className="wf-actions">
          <button type="button" className="wf-btn" disabled={busy} onClick={onRun}>
            <Icon.play /> {busy ? 'Scrubbing…' : moduleDef.runLabel}
          </button>
          <button type="button" className="wf-btn wf-btn--ghost" onClick={() => { setFiles([]); setPayloadText(SAMPLE_PAYLOAD); }}>
            Reset
          </button>
        </div>
        {error && <div className="wf-error">{error}</div>}
      </section>

      {result && (
        <ResultsPanel
          title="Scrub results"
          data={result}
          downloadName={`claim-right_${sample?.id || 'scrub'}`}
          textReport={chatContext}
          printableRef={printRef}
        >
          <div ref={printRef}>
            {/* Rich v5 output (has findings[] from process map) */}
            {result.findings ? (
              <div>
                {/* Status banner */}
                <div style={{
                  padding: '12px 16px', borderRadius: 8, marginBottom: 16,
                  background: result.scrubStatus === 'passed' ? '#ecfdf5' : '#fef2f2',
                  border: `1px solid ${result.scrubStatus === 'passed' ? '#a7f3d0' : '#fecaca'}`,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: result.scrubStatus === 'passed' ? '#065f46' : '#991b1b' }}>
                    {result.scrubStatus === 'passed' ? 'CLEAN — Ready to submit' : 'FAILED — Do not submit'}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 4, color: 'var(--dm-ink)' }}>{result.summary}</div>
                </div>

                {/* Findings */}
                {(result.findings || []).length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700 }}>Findings ({result.findings.length})</h3>
                    {result.findings.map((f, idx) => (
                      <details key={idx} style={{ marginBottom: 10, border: '1px solid var(--dm-border)', borderRadius: 8, padding: '10px 14px' }} open={idx === 0}>
                        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--dm-ink)' }}>
                          <span style={{ color: f.severity === 'error' ? '#dc2626' : f.severity === 'warning' ? '#d97706' : '#6b7280' }}>
                            {(f.severity || 'error').toUpperCase()}
                          </span>
                          {' · '}{f.type?.replace(/_/g, ' ')} · {f.procedureCode}
                          {f.submittedUnits > 0 && ` (${f.submittedUnits} units)`}
                        </summary>
                        <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.7, color: 'var(--dm-ink)' }}>
                          {f.procedureDescription && <div><b>Procedure:</b> {f.procedureDescription}</div>}
                          {f.unitBasis && <div><b>Unit basis:</b> {f.unitBasis}</div>}
                          {f.mueLimit > 0 && <div><b>MUE limit:</b> {f.mueLimit} · <b>Submitted:</b> {f.submittedUnits} · <b>Excess:</b> {f.excessUnits}</div>}
                          {f.submittedDoseEquivalentMg > 0 && <div><b>Submitted dose:</b> {f.submittedDoseEquivalentMg.toLocaleString()} mg · <b>MUE dose:</b> {f.mueDoseEquivalentMg?.toLocaleString()} mg</div>}
                          {f.rationale && <div style={{ marginTop: 6 }}><b>Rationale:</b> {f.rationale}</div>}
                          {f.payerImpact && <div style={{ marginTop: 4, color: '#dc2626' }}><b>Payer impact:</b> {f.payerImpact}</div>}
                          {f.recommendedAction && <div style={{ marginTop: 4, color: '#1d4ed8' }}><b>Action:</b> {f.recommendedAction}</div>}
                          {(f.correctionExamples || []).length > 0 && (
                            <div style={{ marginTop: 6 }}>
                              <b>Correction examples:</b>
                              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                {f.correctionExamples.map((ex, i) => (
                                  <li key={i}>{ex.actualDose} → {ex.correctUnits} units</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {f.source?.ruleSet && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--dm-muted)' }}>Source: {f.source.ruleSet} · {f.source.editType}</div>}
                        </div>
                      </details>
                    ))}
                  </div>
                )}

                {/* Natural Language Analysis */}
                {result.naturalLanguageAnalysis && (
                  <details style={{ marginBottom: 16, border: '1px solid var(--dm-border)', borderRadius: 8, padding: '10px 14px' }} open>
                    <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 700, color: 'var(--dm-ink)' }}>Policy Analysis</summary>
                    <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.7, color: 'var(--dm-ink)', whiteSpace: 'pre-wrap' }}>
                      {result.naturalLanguageAnalysis}
                    </div>
                  </details>
                )}

                {/* Correction Sources */}
                {(result.correctionSources || []).length > 0 && (
                  <details style={{ marginBottom: 16, border: '1px solid var(--dm-border)', borderRadius: 8, padding: '10px 14px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--dm-ink)' }}>Sources ({result.correctionSources.length})</summary>
                    <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--dm-muted)' }}>
                      {result.correctionSources.map((s, i) => (
                        <li key={i} style={{ marginBottom: 4 }}><b>{s.sourceType}</b> · {s.document} — {s.usedFor}</li>
                      ))}
                    </ul>
                  </details>
                )}

                {result.requiresHumanReview && (
                  <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                    <b>Human review required:</b> {result.humanReviewReason}
                  </div>
                )}
              </div>
            ) : (
              /* Legacy simple output (deterministic only, no process map) */
              <div>
                <dl className="wf-kv">
                  <div><dt>Issues</dt><dd>{(result.issues || []).length}</dd></div>
                  <div><dt>NCCI pairs</dt><dd>{(result.ncci_pairs || []).length}</dd></div>
                  <div><dt>MUE caps</dt><dd>{Object.keys(result.mue_caps || {}).length}</dd></div>
                  <div><dt>Global periods</dt><dd>{Object.keys(result.global_periods || {}).length}</dd></div>
                </dl>
                <div style={{ marginTop: 10 }}>
                  {(result.issues || []).length === 0 ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--dm-ink)' }}>
                      No edits flagged. This claim is clean.
                    </p>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--dm-ink)' }}>
                      {(result.issues || []).map((i, idx) => (
                        <li key={idx} style={{ marginBottom: 6 }}>
                          <b>{(i.severity || 'review').toUpperCase()}</b>
                          &nbsp;· {KIND_META[i.kind]?.label || (i.kind || 'edit').toUpperCase()}
                          {(i.codes || []).length > 0 ? ` · ${i.codes.join(' + ')}` : ''}
                          <div style={{ color: 'var(--dm-muted)', marginTop: 2 }}>{i.message}</div>
                          {i.fix && <div style={{ color: '#1d4ed8', marginTop: 2, fontSize: 12 }}><b>Fix:</b> {i.fix}</div>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {result.policy_analysis && (
                  <div style={{ marginTop: 20, borderTop: '1px solid var(--dm-border)', paddingTop: 16 }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: 'var(--dm-ink)' }}>Policy Analysis</h3>
                    {(result.policy_analysis.graph_results || []).map((gr, idx) => (
                      <details key={idx} style={{ marginBottom: 10, border: '1px solid var(--dm-border)', borderRadius: 8, padding: '8px 12px' }} open={idx === 0}>
                        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--dm-ink)' }}>
                          {gr.graph_name}
                          {gr.score > 0 && <span style={{ fontWeight: 400, color: 'var(--dm-muted)', marginLeft: 8 }}>score: {(gr.score * 100).toFixed(0)}%</span>}
                        </summary>
                        <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.6, color: 'var(--dm-ink)', whiteSpace: 'pre-wrap' }}>{gr.analysis}</div>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </ResultsPanel>
      )}
      <ExampleModal open={showExample} onClose={() => setShowExample(false)} title="Example: ClaimRight JSON Input" content={CLAIMRIGHT_EXAMPLE} />
    </WorkflowLayout>
  );
}
